import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";
import { IoAlertCircleOutline, IoCheckmarkCircleOutline, IoCloseOutline } from "react-icons/io5";
import styled, { keyframes } from "styled-components";

import { INFO_TIMEOUT } from "../constants";

type Level = "error" | "success";

type Notification = {
    id: number;
    level: Level;
    text: string;
};

type NotificationsContextValue = {
    error(text: string): void;
    success(text: string): void;
};

const NotificationsContext = createContext<NotificationsContextValue>({
    error: () => undefined,
    success: () => undefined,
});

const slideIn = keyframes`
    from {
        opacity: 0;
        transform: translateX(12px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
`;

const StyledNotifications = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-width: min(520px, calc(100vw - 48px));
    min-width: min(380px, calc(100vw - 48px));
    position: fixed;
    right: 24px;
    top: 24px;
    z-index: 9999;

    .notification {
        align-items: flex-start;
        animation: ${ slideIn } 0.15s ease-out;
        border: 1px solid;
        border-radius: 8px;
        box-shadow: 0 8px 20px rgba(9, 30, 66, 0.2);
        display: flex;
        font-size: 1rem;
        font-weight: 500;
        gap: 12px;
        line-height: 1.45;
        padding: 16px 18px;
        pointer-events: auto;

        &.success {
            background: #e3fcef;
            border-color: #abf5d1;
            color: #006644;
        }

        &.error {
            background: #ffebe6;
            border-color: #ffbdad;
            color: #bf2600;
        }

        .text {
            flex: 1;
            overflow-wrap: anywhere;
        }

        svg {
            flex: none;
            font-size: 1.35rem;
        }

        button {
            background: transparent;
            border: none;
            color: inherit;
            cursor: pointer;
            display: flex;
            font-size: 1.25rem;
            opacity: 0.6;
            padding: 0;

            &:hover {
                opacity: 1;
            }
        }
    }
`;

export function NotificationsProvider(props: { children: ReactNode }) {
    const [notifications, setNotifications] = useState<Notification[]>([]);

    const nextID = useRef(0);
    const timers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

    useEffect(
        () => () => {
            for (const timer of timers.current) clearTimeout(timer);
            timers.current.clear();
        },
        [],
    );

    const dismiss = useCallback(
        (id: number) => setNotifications((current) => current.filter((notification) => notification.id !== id)),
        [],
    );

    const push = useCallback(
        (level: Level, text: string) => {
            const id = nextID.current++;
            setNotifications((current) => [...current, { id, level, text }]);
            const timer = setTimeout(
                () => {
                    timers.current.delete(timer);
                    dismiss(id);
                },
                INFO_TIMEOUT,
            );
            timers.current.add(timer);
        },
        [dismiss],
    );

    const value = useMemo(
        () => (
            {
                error: (text: string) => push("error", text),
                success: (text: string) => push("success", text),
            }
        ),
        [push],
    );

    return (
        <NotificationsContext.Provider value={ value }>
            { props.children }
            <StyledNotifications>
                {
                    notifications.map(
                        (notification) => (
                            <div className={ `notification ${ notification.level }` } key={ notification.id }>
                                {
                                    notification.level === "success"
                                        ? <IoCheckmarkCircleOutline />
                                        : <IoAlertCircleOutline />
                                }
                                <span className="text">{ notification.text }</span>
                                <button
                                    aria-label="Dismiss"
                                    onClick={ () => dismiss(notification.id) }
                                    type="button"
                                >
                                    <IoCloseOutline />
                                </button>
                            </div>
                        )
                    )
                }
            </StyledNotifications>
        </NotificationsContext.Provider>
    );
}

export default function useNotifications() {
    return useContext(NotificationsContext);
}

/** Turns a mutation's `errors` payload into one red notification per entry. */
export function notifyMutationErrors(
    notifications: NotificationsContextValue,
    errors: { message: string, path?: string[] }[],
) {
    for (const error of errors) {
        const path = error.path?.join?.(".");
        notifications.error(path ? `${ path }: ${ error.message }` : error.message);
    }
}
